import { createParser } from 'eventsource-parser';
import { StreamEvent } from './types';

export interface SSEParserOptions {
  /** Called for each parsed SSE event (text, done, ask_user_question, etc.) */
  onEvent: (event: StreamEvent) => void;
  /** Called when a heartbeat comment (`:heartbeat`) is received from the server. */
  onHeartbeat?: () => void;
  /** Called with the latest seqId every time an event with `id:` is received. */
  onSeqId?: (seqId: number) => void;
}

/**
 * Parses an SSE (Server-Sent Events) stream from a Response and calls
 * `onEvent` for each fully-parsed event. Uses `eventsource-parser` for
 * robust SSE line parsing (handles multi-byte characters, edge cases).
 *
 * Also detects `:heartbeat` comments and notifies via `onHeartbeat` callback.
 */
export async function processSSEStream(
  response: Response,
  onEventOrOptions: ((event: StreamEvent) => void) | SSEParserOptions,
): Promise<void> {
  const options: SSEParserOptions =
    typeof onEventOrOptions === 'function'
      ? { onEvent: onEventOrOptions }
      : onEventOrOptions;
  const { onEvent, onHeartbeat, onSeqId } = options;
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();

  const parser = createParser({
    onEvent: sseEvent => {
      const seqId = sseEvent.id ? parseInt(sseEvent.id, 10) : undefined;
      if (seqId !== undefined && !isNaN(seqId)) {
        onSeqId?.(seqId);
      }

      try {
        const data = JSON.parse(sseEvent.data);
        const payload =
          data && typeof data === 'object' && 'payload' in data
            ? data.payload ?? {}
            : data;

        const resolvedSeqId =
          seqId ??
          (data && typeof data === 'object' && 'seqId' in data
            ? (data.seqId as number | undefined)
            : undefined);

        if (resolvedSeqId !== undefined && resolvedSeqId !== seqId) {
          onSeqId?.(resolvedSeqId);
        }

        onEvent(
          buildStreamEvent(sseEvent.event ?? '', resolvedSeqId, data, payload),
        );
      } catch (e: unknown) {
        // Log malformed JSON for debugging but don't break the stream
        // eslint-disable-next-line no-console
        console.warn(
          '[sse-parser] Skipping malformed SSE event data:',
          e instanceof Error ? e.message : String(e),
        );
      }
    },
    onComment: comment => {
      if (comment === 'heartbeat' || comment.startsWith('heartbeat')) {
        onHeartbeat?.();
      }
    },
  });

  for (;;) {
    let done: boolean;
    let value: Uint8Array | undefined;
    try {
      ({ done, value } = await reader.read());
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      throw err;
    }
    if (done) break;

    parser.feed(decoder.decode(value, { stream: true }));
  }
}

/** Build a typed StreamEvent from parsed SSE data and payload. */
function buildStreamEvent(
  eventType: string,
  seqId: number | undefined,
  data: Record<string, unknown>,
  payload: Record<string, unknown>,
): StreamEvent {
  return {
    type: eventType as StreamEvent['type'],
    seqId,
    content: extractString(payload, 'content'),
    metrics: extractField(payload, 'metrics'),
    questions: extractField(payload, 'questions'),
    phase:
      extractString(data, 'phase') ||
      extractString(payload, 'phase') ||
      undefined,
    nextPhase: extractField(payload, 'nextPhase'),
    taskNumber: extractField(payload, 'taskNumber'),
    taskTitle: extractField(payload, 'taskTitle'),
    taskChunkType: extractField(payload, 'taskChunkType'),
    errorMessage: extractField(payload, 'errorMessage'),
    integrated: extractField(payload, 'integrated'),
    lifecycleAction: extractField(payload, 'lifecycleAction'),
    branch: extractField(payload, 'branch'),
    branchAction: extractField(payload, 'branchAction'),
    reason: extractField(payload, 'reason'),
    phaseAttemptId: extractField(payload, 'phaseAttemptId'),
    missingReasonCodes: extractField(payload, 'missingReasonCodes'),
    missingConcepts: extractField(payload, 'missingConcepts'),
    code: extractField(payload, 'code'),
    resetTime: extractField(payload, 'resetTime'),
    timezone: extractField(payload, 'timezone'),
    tasks: extractField(payload, 'tasks'),
    approaches: extractField(payload, 'approaches'),
    review: extractField(payload, 'review'),
    parallelSlot: extractField(payload, 'parallelSlot'),
    totalSlots: extractField(payload, 'totalSlots'),
    worktreePath: extractField(payload, 'worktreePath'),
  };
}

/** Safely extract a string field from an object, defaulting to ''. */
function extractString(obj: unknown, key: string): string {
  if (obj && typeof obj === 'object' && key in obj) {
    return String((obj as Record<string, unknown>)[key] ?? '');
  }
  return '';
}

/** Safely extract a field from an object if it exists. */
function extractField<T = unknown>(obj: unknown, key: string): T | undefined {
  if (obj && typeof obj === 'object' && key in obj) {
    return (obj as Record<string, unknown>)[key] as T;
  }
  return undefined;
}
